import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faEye,
  faDownload,
  faSave,
  faTimes,
  faUpload,
  faFileAlt,
  faBook,
  faNewspaper,
  faSearch,
  faFilter,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faCalendarAlt,
  faUser,
  faTag,
  faGlobe,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../utils/api';

const ResourceManager = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [categories, setCategories] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // 表单数据
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'report',
    category: '',
    cover: '',
    fileUrl: '',
    fileSize: 0,
    fileSizeText: '',
    publishDate: new Date().toISOString().split('T')[0],
    isPublished: true,
    isPinned: false,
    tags: [],
    author: '云帐天下',
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: []
    }
  });

  // 富文本编辑器配置
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'align', 'link', 'image', 'video'
  ];

  useEffect(() => {
    loadResources();
    loadCategories();
  }, [currentPage, searchTerm, filterType, filterCategory, filterStatus]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterType) params.type = filterType;
      if (filterCategory) params.category = filterCategory;
      if (filterStatus) params.status = filterStatus;

      const response = await api.get('/resources/admin/list', { params });
      
      if (response.data.status === 'success') {
        setResources(response.data.data);
        setTotalPages(response.data.pagination.total);
        setTotalCount(response.data.pagination.totalCount);
      }
    } catch (error) {
      console.error('加载资源失败:', error);
      setError('加载资源失败');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/resources/categories');
      if (response.data.status === 'success') {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('加载分类失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingResource(null);
    setFormData({
      title: '',
      description: '',
      type: 'report',
      category: '',
      cover: '',
      fileUrl: '',
      fileSize: 0,
      fileSizeText: '',
      publishDate: new Date().toISOString().split('T')[0],
      isPublished: true,
      isPinned: false,
      tags: [],
      author: '云帐天下',
      seo: {
        metaTitle: '',
        metaDescription: '',
        keywords: []
      }
    });
    setShowModal(true);
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setFormData({
      ...resource,
      publishDate: new Date(resource.publishDate).toISOString().split('T')[0],
      tags: resource.tags || [],
      seo: resource.seo || {
        metaTitle: '',
        metaDescription: '',
        keywords: []
      }
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const saveData = {
        ...formData,
        publishDate: new Date(formData.publishDate).toISOString()
      };

      let response;
      if (editingResource) {
        response = await api.put(`/resources/admin/${editingResource._id}`, saveData);
      } else {
        response = await api.post('/resources/admin/create', saveData);
      }

      if (response.data.status === 'success') {
        setSuccess(editingResource ? '资源更新成功' : '资源创建成功');
        setShowModal(false);
        loadResources();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('保存资源失败:', error);
      setError(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个资源吗？')) return;

    try {
      const response = await api.delete(`/resources/admin/${id}`);
      if (response.data.status === 'success') {
        setSuccess('资源删除成功');
        loadResources();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('删除资源失败:', error);
      setError(error.response?.data?.message || '删除失败');
    }
  };

  const handleFileUpload = async (event, type = 'file') => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      if (type === 'file') {
        setUploadingFile(true);
      } else {
        setUploadingCover(true);
      }

      const uploadData = new FormData();
      uploadData.append('file', file);

      const endpoint = type === 'file' ? '/resources/admin/upload' : '/admin/upload-image';
      const response = await api.post(endpoint, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status === 'success') {
        if (type === 'file') {
          setFormData(prev => ({
            ...prev,
            fileUrl: response.data.data.fileUrl,
            fileSize: response.data.data.fileSize,
            fileSizeText: response.data.data.fileSizeText
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            cover: response.data.data.url
          }));
        }
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      setError('文件上传失败');
    } finally {
      if (type === 'file') {
        setUploadingFile(false);
      } else {
        setUploadingCover(false);
      }
    }
  };

  const handleTagAdd = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeywordAdd = (keyword) => {
    if (keyword && !formData.seo.keywords.includes(keyword)) {
      setFormData(prev => ({
        ...prev,
        seo: {
          ...prev.seo,
          keywords: [...prev.seo.keywords, keyword]
        }
      }));
    }
  };

  const handleKeywordRemove = (keywordToRemove) => {
    setFormData(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        keywords: prev.seo.keywords.filter(keyword => keyword !== keywordToRemove)
      }
    }));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'report': return faFileAlt;
      case 'whitepaper': return faBook;
      case 'guide': return faNewspaper;
      case 'case_study': return faUser;
      default: return faFileAlt;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'report': return '行业报告';
      case 'whitepaper': return '技术白皮书';
      case 'guide': return '操作指南';
      case 'case_study': return '案例研究';
      default: return '未知类型';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = !searchTerm || 
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !filterType || resource.type === filterType;
    const matchesCategory = !filterCategory || resource.category === filterCategory;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'published' && resource.isPublished) ||
      (filterStatus === 'draft' && !resource.isPublished);
    
    return matchesSearch && matchesType && matchesCategory && matchesStatus;
  });

  return (
    <div className="resource-manager">
      {/* 头部 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">资源管理</h2>
          <p className="text-gray-600 mt-1">管理行业报告、白皮书等资源内容</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          添加资源
        </button>
      </div>

      {/* 消息提示 */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <FontAwesomeIcon icon={faCheck} className="mr-2" />
          {success}
        </div>
      )}

      {/* 搜索和筛选 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="搜索资源..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">所有类型</option>
            <option value="report">行业报告</option>
            <option value="whitepaper">技术白皮书</option>
            <option value="guide">操作指南</option>
            <option value="case_study">案例研究</option>
          </select>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">所有分类</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">所有状态</option>
            <option value="published">已发布</option>
            <option value="draft">草稿</option>
          </select>
        </div>
      </div>

      {/* 资源列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-gray-400 animate-spin mb-4" />
            <p className="text-gray-600">加载中...</p>
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faFileAlt} className="text-4xl text-gray-400 mb-4" />
            <p className="text-gray-600">暂无资源</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    资源信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    类型/分类
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    统计
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    发布时间
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResources.map((resource) => (
                  <tr key={resource._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {resource.cover && (
                          <img
                            src={resource.cover}
                            alt={resource.title}
                            className="h-12 w-12 rounded-lg object-cover mr-4"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 line-clamp-1">
                            {resource.title}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-2">
                            {resource.description}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            作者: {resource.author}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-1">
                          <FontAwesomeIcon icon={getTypeIcon(resource.type)} className="mr-1" />
                          {getTypeLabel(resource.type)}
                        </span>
                        <span className="text-sm text-gray-600">{resource.category}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <FontAwesomeIcon icon={faDownload} className="mr-1 text-gray-400" />
                          {resource.downloadCount} 下载
                        </div>
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faEye} className="mr-1 text-gray-400" />
                          {resource.viewCount} 浏览
                        </div>
                        {resource.fileSizeText && (
                          <div className="text-xs text-gray-500 mt-1">
                            {resource.fileSizeText}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-1 ${
                          resource.isPublished 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {resource.isPublished ? '已发布' : '草稿'}
                        </span>
                        {resource.isPinned && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            置顶
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-gray-400" />
                        {formatDate(resource.publishDate)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        创建: {formatDate(resource.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(resource)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="编辑"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDelete(resource._id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="删除"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{' '}
                  条，共 <span className="font-medium">{totalCount}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    上一页
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 编辑/创建模态框 */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingResource ? '编辑资源' : '创建资源'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 基本信息 */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                    基本信息
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      标题 *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入资源标题"
                      maxLength={200}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      描述 *
                    </label>
                    <ReactQuill
                      value={formData.description}
                      onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                      modules={quillModules}
                      formats={quillFormats}
                      className="bg-white"
                      style={{ height: '200px' }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-16">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        类型 *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="report">行业报告</option>
                        <option value="whitepaper">技术白皮书</option>
                        <option value="guide">操作指南</option>
                        <option value="case_study">案例研究</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        分类 *
                      </label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="请输入分类"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      作者
                    </label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入作者"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      发布日期
                    </label>
                    <input
                      type="date"
                      value={formData.publishDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, publishDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isPublished}
                        onChange={(e) => setFormData(prev => ({ ...prev, isPublished: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">发布</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isPinned}
                        onChange={(e) => setFormData(prev => ({ ...prev, isPinned: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">置顶</span>
                    </label>
                  </div>
                </div>

                {/* 文件和媒体 */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faUpload} className="mr-2" />
                    文件和媒体
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      封面图片
                    </label>
                    <div className="flex items-center space-x-4">
                      {formData.cover && (
                        <img
                          src={formData.cover}
                          alt="封面预览"
                          className="h-20 w-20 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'cover')}
                          className="hidden"
                          id="cover-upload"
                        />
                        <label
                          htmlFor="cover-upload"
                          className="cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          {uploadingCover ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                          ) : (
                            <FontAwesomeIcon icon={faUpload} className="mr-2" />
                          )}
                          上传封面
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      资源文件 *
                    </label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-4">
                        <input
                          type="file"
                          onChange={(e) => handleFileUpload(e, 'file')}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          {uploadingFile ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                          ) : (
                            <FontAwesomeIcon icon={faUpload} className="mr-2" />
                          )}
                          上传文件
                        </label>
                        {formData.fileSizeText && (
                          <span className="text-sm text-gray-500">
                            {formData.fileSizeText}
                          </span>
                        )}
                      </div>
                      {formData.fileUrl && (
                        <div className="text-sm text-green-600">
                          <FontAwesomeIcon icon={faCheck} className="mr-1" />
                          文件已上传
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 标签 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      标签
                    </label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            <FontAwesomeIcon icon={faTag} className="mr-1" />
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleTagRemove(tag)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              <FontAwesomeIcon icon={faTimes} className="text-xs" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="text"
                        placeholder="输入标签后按回车添加"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleTagAdd(e.target.value.trim());
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* SEO设置 */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 flex items-center mb-2">
                      <FontAwesomeIcon icon={faGlobe} className="mr-2" />
                      SEO设置
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          SEO标题
                        </label>
                        <input
                          type="text"
                          value={formData.seo.metaTitle}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            seo: { ...prev.seo, metaTitle: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="SEO标题（留空自动生成）"
                          maxLength={60}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          SEO描述
                        </label>
                        <textarea
                          value={formData.seo.metaDescription}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            seo: { ...prev.seo, metaDescription: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="SEO描述（留空自动生成）"
                          rows={3}
                          maxLength={160}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          关键词
                        </label>
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {formData.seo.keywords.map((keyword, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                {keyword}
                                <button
                                  type="button"
                                  onClick={() => handleKeywordRemove(keyword)}
                                  className="ml-1 text-green-600 hover:text-green-800"
                                >
                                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                </button>
                              </span>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="输入关键词后按回车添加"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleKeywordAdd(e.target.value.trim());
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.title || !formData.description || !formData.fileUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                )}
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceManager;